/**
 * The default value configured on a certificate or customer custom field definition
 * (CERTCAP-68218 added the setting).
 *
 * Defaults exist only to pre-fill a form at page load, while a certificate or customer is
 * being created. Once the form is on screen they are ordinary values: the user can change
 * one, or clear it and save it blank. Editing an existing record never consults them.
 *
 * React-free so both `src/app/**` slices and `src/features/**` components can import it.
 */

/** A custom field definition as returned by the certificate/customer custom field endpoints. */
export interface CustomFieldDefinition {
    id?: number | string;
    /** Set only once a value row exists for the field, i.e. never while creating. */
    fieldId?: number | string;
    exists?: boolean;
    type?: string;
    defaultValue?: string | null;
}

/** A stored custom field answer: a string, or the selected members of a multi-select. */
export type CustomFieldValue = string | string[] | null | undefined;

export type CustomFieldValues = Record<string, CustomFieldValue>;

/**
 * The key the value maps use. `fieldId` takes over once the field has a saved value row,
 * which only happens on an existing record.
 */
export const getCustomFieldKey = (field?: CustomFieldDefinition): string | undefined => {
    const key = field?.exists ? field.fieldId : field?.id;
    return key === null || key === undefined ? undefined : String(key);
};

/**
 * The definition's default in the shape the value maps store: an array for a multi-select
 * (the API sends it pipe separated), a plain string otherwise. `null` when there is none.
 */
export const getCustomFieldDefaultValue = (field?: CustomFieldDefinition): CustomFieldValue => {
    const raw = field?.defaultValue;
    if (raw === null || raw === undefined || String(raw).trim() === "") {
        return null;
    }
    if (field?.type === "multi-select") {
        const selected = String(raw)
            .split("|")
            .map(value => value.trim())
            .filter(value => value !== "");
        return selected.length ? selected : null;
    }
    return String(raw);
};

/** The default as a text input renders it — a single string, or "" when there is none. */
export const getCustomFieldDefaultText = (field?: CustomFieldDefinition): string => {
    const value = getCustomFieldDefaultValue(field);
    if (value === null || value === undefined) {
        return "";
    }
    return Array.isArray(value) ? value.join("|") : value;
};

/**
 * The defaults to pre-fill a blank create form with: one entry per definition that has a
 * default and no answer yet. A key already present — even holding a blank — is the user's
 * own answer and is left alone, so clearing a default never puts it back.
 */
export const collectMissingCustomFieldDefaults = (
    definitions: CustomFieldDefinition[] | null | undefined,
    values: CustomFieldValues | null | undefined
): CustomFieldValues => {
    const defaults: CustomFieldValues = {};
    (definitions || []).forEach(field => {
        const key = getCustomFieldKey(field);
        if (key === undefined || Object.prototype.hasOwnProperty.call(values || {}, key)) {
            return;
        }
        const defaultValue = getCustomFieldDefaultValue(field);
        if (defaultValue !== null) {
            defaults[key] = defaultValue;
        }
    });
    return defaults;
};
